import { ReactNode } from "react";
import { Button, Card, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/pro-solid-svg-icons";
import FormFieldLabel from "@components/Forms/FormFieldLabel/FormFieldLabel";
import SearchField from "@components/Forms/SearchField/SearchField";
import { BatchActionPill, BatchAction } from "./BatchActionPill";
import "./CatalogIndex.css";

export interface AdminListCardSearchProps {
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onClear: () => void;
  label?: string;
  /** Right-aligned on the label row, e.g. a "Show mine only" toggle. */
  labelAccessory?: ReactNode;
  /** Beside the search field on its row, top-aligned, e.g. filter icons. */
  trailing?: ReactNode;
}

export interface AdminListCardSecondaryAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface AdminListCardProps {
  title: string;
  onNew?: () => void;
  newButtonLabel?: string;
  /** When set, the new button is disabled and shows this note (e.g. the plan lacks the feature). */
  newLockedReason?: string;
  secondaryAction?: AdminListCardSecondaryAction;
  searchProps?: AdminListCardSearchProps;
  batchActions?: BatchAction[];
  selectedCount?: number;
  /** Renders in the same flex row as the batch pill — sits alone at the
   *  left when nothing is selected, and after the pill (bumped right) once
   *  something is. E.g. a running total that should occupy the pill's slot. */
  extraInfo?: ReactNode;
  /** Renders without the card border/shadow and title — for a list nested
   *  inside an AdminFormPage tab, where the tab bar already serves as the
   *  header. */
  bare?: boolean;
  /** "page" renders the list as a top-level page (e.g. Projects, reached from
   *  the main nav) rather than a card inside a settings frame: a page-size h1
   *  and the header on the page background, with no card border, shadow or
   *  white background. The caller supplies any box around its table. */
  variant?: "card" | "page";
  /** bare only: hides `children` and shows `emptyMessage` in the header row
   *  instead of the batch pill — mirrors Edit Blueprint's item tabs
   *  (Products/Resources/Governance/Expense). */
  isEmpty?: boolean;
  emptyMessage?: string;
  children: ReactNode;
}

export const AdminListCard = ({
  title,
  onNew,
  newButtonLabel = "+ New",
  newLockedReason,
  secondaryAction,
  searchProps,
  batchActions,
  selectedCount = 0,
  extraInfo,
  bare = false,
  variant = "card",
  isEmpty = false,
  emptyMessage,
  children,
}: AdminListCardProps) => {
  const showBatchPill =
    !!batchActions && batchActions.length > 0 && selectedCount > 0;
  const showActionRow = showBatchPill || !!searchProps || !!extraInfo;

  const actionButtons = (secondaryAction || onNew) && (
    <div className="admin-list-card__title-actions">
      {secondaryAction && (
        <Button
          className="btnSeawhite"
          onClick={secondaryAction.onClick}
          disabled={secondaryAction.disabled}
        >
          {secondaryAction.label}
        </Button>
      )}
      {onNew && !newLockedReason && (
        <Button className="squareGreenButton" onClick={onNew}>
          {newButtonLabel}
        </Button>
      )}
      {onNew && newLockedReason && (
        // A disabled button gets no pointer events or focus, so the note
        // lives on a focusable wrapper that tooltips and screen readers reach.
        <span
          className="d-inline-block"
          title={newLockedReason}
          tabIndex={0}
          role="group"
          aria-label={newLockedReason}
        >
          <Button
            className="squareGreenButton"
            disabled
            style={{ pointerEvents: "none" }}
            aria-label={`${newButtonLabel}. ${newLockedReason}`}
          >
            <FontAwesomeIcon icon={faLock} style={{ marginRight: 6 }} />
            {newButtonLabel}
          </Button>
        </span>
      )}
    </div>
  );

  const actionRow = showActionRow && (
    <div className="admin-list-card__action-row">
      {showBatchPill && (
        <BatchActionPill
          selectedCount={selectedCount}
          actions={batchActions!}
        />
      )}
      {extraInfo}
      {searchProps && (
        <div className="admin-list-card__search-wrapper">
          <div
            className={`admin-list-card__search-label-row${
              searchProps.labelAccessory
                ? " admin-list-card__search-row--split"
                : ""
            }`}
          >
            <FormFieldLabel
              label={searchProps.label ?? "Search"}
              className={undefined as unknown as string}
            />
            {searchProps.labelAccessory && (
              <div className="admin-list-card__search-accessory">
                {searchProps.labelAccessory}
              </div>
            )}
          </div>
          <div
            className={`admin-list-card__search-field-row${
              searchProps.trailing ? " admin-list-card__search-row--split" : ""
            }`}
          >
            <Form
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  searchProps.onSubmit(
                    e as unknown as React.FormEvent<HTMLFormElement>
                  );
                }
              }}
              onSubmit={searchProps.onSubmit}
            >
              <SearchField
                placeholder={searchProps.placeholder}
                value={searchProps.value}
                onChange={searchProps.onChange}
                onClick={searchProps.onClear}
                fullWidth
              />
            </Form>
            {searchProps.trailing && (
              <div className="admin-list-card__search-trailing">
                {searchProps.trailing}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  if (bare) {
    const showEmptyMessage = isEmpty && !showBatchPill && !!emptyMessage;
    const hasLeftSlot = showBatchPill || showEmptyMessage;

    return (
      <div className="admin-list-card admin-list-card--bare">
        <div
          className={`admin-list-card__title-row admin-list-card__title-row--bare${
            hasLeftSlot ? " admin-list-card__title-row--split" : ""
          }`}
        >
          {showBatchPill ? (
            <BatchActionPill
              selectedCount={selectedCount}
              actions={batchActions!}
            />
          ) : showEmptyMessage ? (
            <div className="admin-list-card__empty">{emptyMessage}</div>
          ) : null}
          {actionButtons}
        </div>
        {searchProps && (
          <div className="admin-list-card__action-row">
            <div className="admin-list-card__search-wrapper">
              <FormFieldLabel
                label={searchProps.label ?? "Search"}
                className={undefined as unknown as string}
              />
              <Form
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    searchProps.onSubmit(
                      e as unknown as React.FormEvent<HTMLFormElement>
                    );
                  }
                }}
                onSubmit={searchProps.onSubmit}
              >
                <SearchField
                  placeholder={searchProps.placeholder}
                  value={searchProps.value}
                  onChange={searchProps.onChange}
                  onClick={searchProps.onClear}
                  fullWidth
                />
              </Form>
            </div>
          </div>
        )}
        {!isEmpty && children}
      </div>
    );
  }

  if (variant === "page") {
    return (
      <div className="admin-list-card admin-list-card--page">
        <div className="admin-list-card__title-row">
          <h1 className="sansTitle">{title}</h1>
          {actionButtons}
        </div>
        <hr />
        {actionRow}
        {children}
      </div>
    );
  }

  return (
    <div className="admin-list-card legacyPageBleed">
      <Card>
        <Card.Header>
          <div className="admin-list-card__title-row">
            <div className="adminListCardTitle">{title}</div>
            {actionButtons}
          </div>
          <hr />
          {actionRow}
        </Card.Header>
        {children}
      </Card>
    </div>
  );
};
